import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as UiLink, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faTrashAlt, faEnvelopeOpen } from '@fortawesome/free-solid-svg-icons';
import notificationReducer from './notificationReducer';
import {
  SpinnerArea,
  Table,
  TablePagination,
  LinkButton,
  ConfirmModal,
  Button,
} from '../../shared/components';
import notificationService from '../../services/notificationService';
import useServiceReducer from '../../shared/hooks/useServiceReducer';
import Context from '../App/MainProvider/context';
import NotificationContext from './NotificationContext';
import {
  NotificationFilter,
  NotificationItem,
  DeleteNotification,
  MarkNotificationAsRead,
} from '../../services/types/Notification';
import { Cell } from '../../shared/components/Table';
import { dateTimeFormat, safeFind } from '../../shared/misc/utils';

const SR_PATH = '/supervision-report';
const SR_ESPF_PATH = '/supervision-report-espf';
const INITIAL_ESRS_PATH = '/initial-esrs';

export const eventLinks = [
  ['TASK_CREATION', '?tab=1'],
  ['TASK_DELEGATION', '?tab=1'],
  ['N_DAYS_FOR_DUEDATE', '?tab=1'],
  ['TOOLKIT_STARTED', '/toolkit'],
  ['TOOLKIT_FINISHED', '/toolkit'],
  ['ESPF_TOOLKIT_STARTED', '/toolkit/espf/list'],
  ['ESPF_TOOLKIT_FINISHED', '/toolkit/espf/list'],
  ['COMPLIANCE_TABLE_STARTED', '/compliance'],
  ['COMPLIANCE_TABLE_NEW_VERSION', '/compliance'],
  ['ESS_STARTED', '/ess'],
  ['ESS_APPROVED', '/ess'],
  ['NEW_COMMENT_IN_ESS', '/ess/comments'],
  ['ESS_FEEDBACK', '/ess'],
  ['ESS_REVIEW', '/esrs'],
  ['ESS_GH_REVIEW', '/esrs'],
  ['ESMR_STARTED', '/esmr'],
  ['ESMR_APPROVED', '/esmr'],
  ['NEW_COMMENT_IN_ESMR', '/esmr/comments'],
  ['ESMR_FEEDBACK', '/esmr'],
  ['ESMR_REVIEW', '/esrs'],
  ['ESMR_GH_REVIEW', '/esrs'],
  ['ADD_OPERATION_MEMBER', '?tab=1'],
  ['ADD_OPERATION_MEMBER_TO_PRIMARY', '?tab=1'],
  ['SR_STARTED', SR_PATH],
  ['SR_APPROVED', SR_PATH],
  ['SR_REVIEW', SR_PATH],
  ['SR_GH_REVIEW', SR_PATH],
  ['SR_FEEDBACK', SR_PATH],
  ['SR_DUE_DATE', SR_PATH],
  ['SR_ESPF_STARTED', SR_ESPF_PATH],
  ['SR_ESPF_APPROVED', SR_ESPF_PATH],
  ['SR_ESPF_REVIEW', SR_ESPF_PATH],
  ['SR_ESPF_GH_REVIEW', SR_ESPF_PATH],
  ['SR_ESPF_FEEDBACK', SR_ESPF_PATH],
  ['SR_ESPF_DUE_DATE', SR_ESPF_PATH],
  ['INITIAL_ESRS_STARTED', INITIAL_ESRS_PATH],
  ['INITIAL_ESRS_APPROVED', INITIAL_ESRS_PATH],
  ['INITIAL_ESRS_FEEDBACK', INITIAL_ESRS_PATH],
  ['INITIAL_ESRS_REVIEW', INITIAL_ESRS_PATH],
  ['INITIAL_ESRS_GH_REVIEW', INITIAL_ESRS_PATH],
  ['NEW_COMMENT_IN_INITIAL_ESRS', '/initial-esrs/comments'],
  ['ESRS_STARTED', '/esrs'],
  ['ESRS_APPROVED', '/esrs'],
  ['ESRS_FEEDBACK', '/esrs'],
  ['ESRS_REVIEW', '/esrs'],
  ['ESRS_GH_REVIEW', '/esrs'],
  ['NEW_COMMENT_IN_ESRS', '/esrs/comments'],
  ['CONVERGENCE_MISSION_IMPORTED', '/missions'],
  ['ESG_MISSION_CREATED', '/missions'],
].map((x) => ({ eventType: x[0], link: x[1] }));

const Notifications: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { service, notify } = useContext(Context);
  const { reload: reloadNotifications } = useContext(NotificationContext);

  const initFilter: NotificationFilter = {
    pageNumber: 1,
    sortField: 'name',
    sortDescending: false,
  };

  const [data, loading, filter, dispatch, loader, reload] = useServiceReducer(
    notificationService.notifications,
    notificationReducer,
    initFilter
  );

  const pageSize = 10;
  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });
  const changeSort = (column: string, descending: boolean) =>
    dispatch({ type: 'ORDER', column, descending });

  const handleMarkAllAsRead = () => {
    const callback = () => {
      reload();
      reloadNotifications();
    };

    service(() => notificationService.markAllAsRead(), { callback });
  };

  const markAsRead = (id: number) => {
    if (!id) {
      return;
    }

    const payload: MarkNotificationAsRead = { id };
    loader(true);

    const callback = (res: NotificationItem | null) => {
      if (res) {
        reload();
        reloadNotifications();
      } else {
        loader(false);
        notify('Notification marked as Read', t('saveFail'), 'danger');
      }
    };

    service(() => notificationService.markAsRead(payload), { callback, loader });
  };

  const [notificationSelected, setNotificationSelected] = useState<NotificationItem | null>(null);
  const [deleteAllConfirmed, setDeleteAllConfirmed] = useState(false);
  const [markAllConfirmModal, setMarkAllConfirmModal] = useState(false);
  const showDeleteNotificationModal = (notification: NotificationItem) =>
    setNotificationSelected(notification);
  const showDeleteAllModal = () => setDeleteAllConfirmed(true);

  const closeDeleteNotificationModal = () => setNotificationSelected(null);
  const closeDeleteAllModal = () => setDeleteAllConfirmed(false);
  const deleteNotification = () => {
    if (!notificationSelected) {
      return;
    }
    const payload: DeleteNotification = { id: notificationSelected.id };
    loader(true);

    const callback = (res: NotificationItem | null) => {
      setNotificationSelected(null);
      if (res) {
        reload();
        reloadNotifications();
      } else {
        loader(false);
        notify('Delete Notification', t('saveFail'), 'danger');
      }
    };

    service(() => notificationService.deleteNotification(payload), { callback, loader });
  };
  const deleteAll = () => {
    if (!deleteAllConfirmed) {
      return;
    }
    loader(true);

    const callback = () => {
      setDeleteAllConfirmed(false);
      reload();
      reloadNotifications();
      notify('Delete Notification', t('successDelete'), 'success');
    };

    service(() => notificationService.deleteAll(), { callback, loader });
  };

  const handleResultNavegation = (resultItem: NotificationItem) => {
    const { id, event, operationNumber, navigationLink, wasRead } = resultItem;

    const callback = () => {
      reloadNotifications();
      const { link } = safeFind(eventLinks, (e) => e.eventType === event);
      navigate(navigationLink || `/operations/${operationNumber}${link}`);
    };
    if (!wasRead) {
      service(() => notificationService.markAsRead({ id }), { callback, loader });
    } else {
      callback();
    }
  };

  const cells: Cell<NotificationItem>[] = [
    {
      name: 'longDescription',
      column: t('description'),
      value: (x) => x.longDescription,
      render: (x, o) => {
        const classname = o.wasRead ? 'visited' : '';
        return (
          <LinkButton className={classname} text={x} onClick={() => handleResultNavegation(o)} />
        );
      },
      sortDisabled: true,
    },
    {
      name: 'createdOn',
      column: t('date'),
      value: (x) => dateTimeFormat(x.createdOn),
      sortDisabled: true,
    },
    {
      column: t('actions'),
      render: (_, obj) => (
        <div className="d-flex flex-row align-items-center px-2">
          <LinkButton
            onClick={() => markAsRead(obj.id)}
            icon={obj.wasRead ? faEnvelopeOpen : faEnvelope}
            visited={obj.wasRead}
          />
          <div role="presentation" onClick={() => showDeleteNotificationModal(obj)}>
            {obj.wasRead ? (
              <FontAwesomeIcon className="visited" icon={faTrashAlt} />
            ) : (
              <UiLink color="nothing" to="#">
                <FontAwesomeIcon icon={faTrashAlt} />
              </UiLink>
            )}
          </div>
        </div>
      ),
      sortDisabled: true,
    },
  ];

  return (
    <>
      <div className="details-bar">
        <div className="content d-flex w-100">
          {data && data.total > 0 ? (
            <>
              <h4>
                {t('showingNotifications', { from: data.from, to: data.to, total: data.total })}
              </h4>
              <div className="mr-auto" />
              <Button
                className="btn btn-primary px-4 py-1"
                onClick={() => setMarkAllConfirmModal(true)}
                text={t('markAllAsRead')}
              />
              <Button
                className="btn btn-primary px-4 py-1"
                onClick={showDeleteAllModal}
                text={t('deleteAll')}
              />
            </>
          ) : (
            t('thereIsNoNotification')
          )}
        </div>
      </div>
      <SpinnerArea loading={loading}>
        {data && data.total > 0 && (
          <>
            <Table
              data={data.list}
              cells={cells}
              keyFunc={(x) => x.id}
              sortData={filter}
              changeSort={changeSort}
            />
            <TablePagination
              pageSize={pageSize}
              pageNumber={filter.pageNumber}
              total={data.total}
              onChangePage={changePage}
            />
          </>
        )}
      </SpinnerArea>
      <ConfirmModal
        title={t('deleteNotification')}
        buttonText={t('delete')}
        visible={notificationSelected != null}
        close={closeDeleteNotificationModal}
        action={deleteNotification}
      >
        {t('deleteNotificationConfirmation')}
      </ConfirmModal>
      <ConfirmModal
        title={t('deleteAllNotification')}
        buttonText={t('deleteAll')}
        visible={deleteAllConfirmed}
        close={closeDeleteAllModal}
        action={deleteAll}
      >
        {t('deleteAllNotificationsConfirmation')}
      </ConfirmModal>
      <ConfirmModal
        title={t('markAllAsReadTitle')}
        visible={markAllConfirmModal}
        close={() => setMarkAllConfirmModal(false)}
        action={handleMarkAllAsRead}
      >
        {t('markAllAsReadConfirmation')}
      </ConfirmModal>
    </>
  );
};

export default Notifications;
